import * as Yup from 'yup';
import { Alert, Typography } from '@mui/material';

import {
  CasEditForm,
  CasMultiRowTable,
  CasRHFTextField,
  CasStackRow,
  IEditForm,
} from 'src/compass/shared/components';
import ContactPhoneField from './ContactPhoneField';

/*
  A contact's name, phones and emails, edited in HubSpot itself (api/Hubspot/Contact).

  The dialog opens on HubSpot's current values, not the app's copy, and the server sends back only
  what changed - so an edit someone made in HubSpot while this was open survives unless the same
  field was changed here too. HubSpot is also what saves come back from: the parent row takes the
  response, not what was typed, since HubSpot may reformat or refuse a value.

  The name is HubSpot's first and last name; one of them may be blank, but not both. Phones are
  typed as digits and kept as HubSpot keeps them (ContactPhoneField).

  An editable contact opens with a light-yellow note that saving changes the contact in HubSpot.
  Outside production only HubSpot test contacts can be edited; any other contact opens read-only
  with the reason above the fields instead of that note, and Update disabled (canEdit /
  cannotEditReason - the button is ContactDetailsButton's canSave).
*/

const digitCount = (value?: string | null) => (value ?? '').replace(/\D/g, '').length;

const phoneSchema = Yup.string()
  .nullable()
  .test(
    'phone-digits',
    'Enter at least 10 digits, or leave it empty',
    (value) => !value?.trim() || digitCount(value) >= 10
  );

const Schema = Yup.object().shape({
  firstname: Yup.string()
    .nullable()
    .test('a-name', 'Enter a first or last name', function hasName(value) {
      return !!value?.trim() || !!this.parent?.lastname?.trim();
    }),
  lastname: Yup.string().nullable(),
  mobilephone: phoneSchema,
  phone: phoneSchema,
  business_phone: phoneSchema,
  email: Yup.string()
    .nullable()
    .trim()
    .email('Enter a valid email address')
    // Additional emails belong to a primary one - the server refuses them without it too.
    .test(
      'primary-for-additional',
      'Enter a primary email, or remove the additional emails',
      function primaryNeeded(value) {
        const additional = (this.parent?.additionalEmails ?? []).some((r: any) => r?.email?.trim());
        return !!value?.trim() || !additional;
      }
    ),
  additionalEmails: Yup.array().of(
    Yup.object().shape({
      email: Yup.string()
        .trim()
        .required('Enter an address, or remove the row')
        .email('Enter a valid email address')
        // HubSpot keeps an address as the primary or an additional one, never both - the server
        // refuses it too. Compared ignoring case, as HubSpot does.
        .test(
          'not-the-primary',
          'Same as the primary email - remove this row',
          function notPrimary(value) {
            const primary = (this.from?.[1]?.value?.email ?? '').trim().toLowerCase();
            return !primary || (value ?? '').trim().toLowerCase() !== primary;
          }
        ),
    })
  ),
});

export default function ContactDetailsForm({ row, onSaved }: IEditForm) {
  const readOnly = !row?.canEdit;

  return (
    <CasEditForm
      // CasMultiRowTable edits rows, not strings, so each address becomes { email } here and goes
      // back to a plain list in onBeforeSave.
      defaultValues={{
        ...row,
        additionalEmails: (row?.additionalEmails ?? []).map((email: string) => ({ email })),
      }}
      entity="Hubspot/Contact"
      schema={Schema}
      mode="onSubmit"
      onBeforeSave={(data: any) => ({
        id: data.id,
        firstname: data.firstname ?? '',
        lastname: data.lastname ?? '',
        email: data.email ?? '',
        mobilephone: data.mobilephone ?? '',
        phone: data.phone ?? '',
        business_phone: data.business_phone ?? '',
        additionalEmails: (data.additionalEmails ?? []).map((r: any) => r.email),
      })}
      onSaved={onSaved}
    >
      {!readOnly && (
        <Typography
          variant="body2"
          // The app's light-yellow highlight (Claim due reminders, Pending emails). mb as on
          // ExternalSceForm's notes: the first field's outlined label sits above its box.
          sx={{ px: 1.5, py: 1, mb: 2, borderRadius: 1, bgcolor: 'warning.lighter' }}
        >
          Note: Changes submitted here will update this contact in Hubspot
        </Typography>
      )}
      {readOnly && row?.cannotEditReason && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {row.cannotEditReason}
        </Alert>
      )}
      <CasStackRow>
        <CasRHFTextField name="firstname" label="First Name" disabled={readOnly} />
        <CasRHFTextField name="lastname" label="Last Name" disabled={readOnly} />
      </CasStackRow>
      <ContactPhoneField name="mobilephone" label="Mobile Phone" disabled={readOnly} />
      <ContactPhoneField name="phone" label="Phone" disabled={readOnly} />
      <ContactPhoneField name="business_phone" label="Business Phone (not logged)" disabled={readOnly} />
      <CasRHFTextField name="email" label="Email (primary)" disabled={readOnly} />
      <CasMultiRowTable
        name="additionalEmails"
        title="Additional emails"
        showColumnHeaders={false}
        width="100%"
        readOnly={readOnly}
        columns={[{ type: 'text', label: 'Additional email', name: 'email', width: '100%' }]}
      />
    </CasEditForm>
  );
}
